const FABRIC_LIMIT = 24;

const RISK_MULTIPLIER = Object.freeze({
  LOW: 0.76,
  MEDIUM: 1,
  ELEVATED: 1.28,
});

const SECTOR_MULTIPLIER = Object.freeze({
  Technology: 1.14,
  Semiconductors: 1.24,
  Software: 1.08,
  Communication: 1.08,
  Consumer: 1.04,
  Automotive: 1.22,
  Financials: 1.03,
  Healthcare: 0.76,
  Energy: 0.88,
  Utilities: 0.58,
});

function clamp(value, minimum, maximum) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return minimum;
  return Math.min(maximum, Math.max(minimum, numeric));
}

function firstText(...values) {
  return values.find((value) => typeof value === "string" && value.trim())?.trim() || null;
}

export function normalizeFabricEvent(input = {}, defaults = {}) {
  const createdAt = firstText(input.createdAt, input.timestamp, defaults.createdAt) || new Date().toISOString();
  const type = firstText(input.type, defaults.type) || "system";
  const source = firstText(input.source, defaults.source) || "nexus";
  const title = firstText(input.title, input.label, defaults.title) || "HQS Ereignis";
  const detail = firstText(input.detail, input.message, defaults.detail) || "Statusänderung empfangen";
  const priority = ["info", "medium", "high", "critical"].includes(input.priority)
    ? input.priority
    : "info";
  const sourceId = firstText(input.sourceId, input.id, defaults.sourceId);
  const explicitFingerprint = firstText(input.fingerprint, defaults.fingerprint);
  const fingerprint = explicitFingerprint || (
    sourceId
      ? (sourceId.startsWith(`${source}:`) ? sourceId : `${source}:${sourceId}`)
      : `${source}:${type}:${title}:${createdAt.slice(0, 19)}`
  );

  return {
    id: fingerprint,
    fingerprint,
    type,
    source,
    title,
    detail,
    priority,
    createdAt,
    targetView: firstText(input.targetView, input.action?.targetView, defaults.targetView),
    metadata: input.metadata && typeof input.metadata === "object" ? input.metadata : {},
  };
}

export function mergeFabricEvents(current = [], incoming = [], limit = FABRIC_LIMIT) {
  const merged = new Map();
  for (const item of [...incoming, ...current]) {
    const normalized = normalizeFabricEvent(item);
    if (!merged.has(normalized.fingerprint)) merged.set(normalized.fingerprint, normalized);
  }
  return [...merged.values()]
    .sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt))
    .slice(0, Math.max(1, limit));
}

function stockStressMultiplier(stock, volatility, correlation) {
  const risk = RISK_MULTIPLIER[String(stock?.risk || "MEDIUM").toUpperCase()] || 1;
  const sector = SECTOR_MULTIPLIER[String(stock?.sector || "")] || 1;
  const correlationCoupling = 0.62 + (clamp(correlation, 0, 100) / 100) * 0.58;
  const volatilityCoupling = 0.68 + clamp(volatility, 0.5, 2.5) * 0.32;
  return risk * sector * correlationCoupling * volatilityCoupling;
}

export function computePortfolioStress(positions = [], configuration = {}) {
  const marketShock = clamp(configuration.marketShock ?? -12, -35, 15);
  const volatility = clamp(configuration.volatility ?? 1.35, 0.5, 2.5);
  const correlation = clamp(configuration.correlation ?? 72, 0, 100);
  const cash = Math.max(0, Number(configuration.cash) || 0);
  const investedPositions = positions.filter((stock) => Number(stock?.quantity) > 0 && Number(stock?.price) >= 0);

  const positionResults = investedPositions.map((stock) => {
    const marketValue = Math.max(0, Number(stock.price) * Number(stock.quantity));
    const multiplier = stockStressMultiplier(stock, volatility, correlation);
    const qualityCushion = clamp((Number(stock.score) - 75) * 0.018, -0.25, 0.3);
    const projectedReturnPct = clamp(marketShock * multiplier + qualityCushion, -58, 28);
    const projectedPnl = marketValue * projectedReturnPct / 100;
    return {
      symbol: String(stock.symbol || "—"),
      sector: String(stock.sector || "Unbekannt"),
      risk: typeof stock.risk === "string" && stock.risk ? stock.risk : "—",
      marketValue,
      multiplier,
      projectedReturnPct,
      projectedPnl,
      projectedValue: Math.max(0, marketValue + projectedPnl),
    };
  });

  const bottomUpDeployed = positionResults.reduce((sum, item) => sum + item.marketValue, 0);
  // fix/pnl-dossier-mapping-and-live-truth: production incident — the
  // per-position sum above collapses to $0 whenever any position's own
  // `price`/`quantity` fields are momentarily null/stale (Number(null)===0
  // still passes the `>= 0` filter above and contributes a $0 market
  // value), even though the portfolio's real deployed-capital total
  // (twinPortfolio.totals.allocated, already validated live) is correct
  // and non-zero. Whenever the caller supplies that trusted total via
  // `configuration.deployedOverride`, it is used as the headline base
  // instead of re-deriving it bottom-up — the per-position breakdown below
  // still uses the bottom-up figures, since it needs an itemized value per
  // position regardless.
  const deployed = Number.isFinite(configuration.deployedOverride) && configuration.deployedOverride > 0
    ? configuration.deployedOverride
    : bottomUpDeployed;
  const projectedPnl = positionResults.reduce((sum, item) => sum + item.projectedPnl, 0);
  const baseValue = deployed + cash;
  const projectedValue = Math.max(0, baseValue + projectedPnl);
  const drawdownPct = baseValue > 0 ? projectedPnl / baseValue * 100 : 0;
  const riskBudgetPct = drawdownPct < 0 ? clamp(Math.abs(drawdownPct) / 12 * 100, 0, 180) : 0;
  const verdict = drawdownPct >= 0
    ? "OPPORTUNITY"
    : riskBudgetPct >= 100
      ? "BLOCKED"
      : riskBudgetPct >= 72
        ? "REVIEW"
        : "WITHIN_BUDGET";

  return {
    marketShock,
    volatility,
    correlation,
    cash,
    deployed,
    baseValue,
    projectedPnl,
    projectedValue,
    drawdownPct,
    riskBudgetPct,
    verdict,
    positions: positionResults.sort((left, right) => left.projectedReturnPct - right.projectedReturnPct),
  };
}

export function buildStressCurve(result, points = 12) {
  const safePoints = Math.max(4, Math.min(30, Math.round(points)));
  return Array.from({ length: safePoints }, (_, index) => {
    const progress = index / (safePoints - 1);
    const easing = progress * progress * (3 - 2 * progress);
    const central = result.baseValue + result.projectedPnl * easing;
    const uncertainty = Math.abs(result.projectedPnl) * (0.025 + progress * 0.14) * result.volatility;
    return {
      progress,
      central,
      upper: central + uncertainty,
      lower: Math.max(0, central - uncertainty),
    };
  });
}

export function formatFabricAge(value, now = Date.now()) {
  const ageSeconds = Math.max(0, Math.round((now - Date.parse(value)) / 1000));
  if (!Number.isFinite(ageSeconds)) return "—";
  if (ageSeconds < 5) return "jetzt";
  if (ageSeconds < 60) return `${ageSeconds}s`;
  return `${Math.floor(ageSeconds / 60)}m`;
}
